import { useState, useCallback } from 'react';
import { PolySpline } from '../components/shapes/PolySpline';
import { Ellipse } from '../components/shapes/Ellipse';
import { Circle } from '../components/shapes/Circle';
import { Rectangle } from '../components/shapes/Rectangle';
import { Square } from '../components/shapes/Square';
import { mirrorPoint as mirrorPointUtil, isAxisAligned } from '../utils/CoordinateUtils';

export const useMirroring = (selectedObjects, setSelectedObjects, gridOffset, gridAreaRef, setMode) => {
    const [isMirrorMode, setIsMirrorMode] = useState(false);
    const [mirrorLinePoints, setMirrorLinePoints] = useState([]);
    const [mirrorPreviewPoint, setMirrorPreviewPoint] = useState(null);

    const performMirrorOperation = useCallback((point1, point2) => {
        if (selectedObjects.length === 0) {
            console.warn("No objects selected for mirror operation.");
            return;
        }

        // Convert input points from World Space to Grid Space
        const gP1 = { x: point1.x - gridOffset.x, y: point1.y - gridOffset.y };
        const gP2 = { x: point2.x - gridOffset.x, y: point2.y - gridOffset.y };
        const dx = gP2.x - gP1.x;
        const dy = gP2.y - gP1.y;
        const a = -dy;
        const b = dx;
        const c = -a * gP1.x - b * gP1.y;

        const mirroredObjects = selectedObjects.map((obj) => {
            const typeName = obj.typeName || obj.type;
            const isRect = (obj.x1 !== undefined && obj.x2 !== undefined);
            const isSquare = (obj.size !== undefined && obj.x !== undefined);

            if (isRect || isSquare) {
                let pts;
                if (typeof obj.getControlPoints === 'function') {
                    pts = obj.getControlPoints();
                } else if (isRect) {
                    pts = [{ x: obj.x1, y: obj.y1 }, { x: obj.x2, y: obj.y2 }];
                } else {
                    pts = [{ x: obj.x, y: obj.y }, { x: obj.x + obj.size, y: obj.y + obj.size }];
                }

                const x1 = pts[0].x;
                const y1 = pts[0].y;
                const x2 = pts[1].x;
                const y2 = pts[1].y;

                const p1 = mirrorPointUtil(x1, y1, a, b, c);
                const p2 = mirrorPointUtil(x2, y1, a, b, c);
                const p3 = mirrorPointUtil(x2, y2, a, b, c);
                const p4 = mirrorPointUtil(x1, y2, a, b, c);

                const minX = Math.min(p1.x, p2.x, p3.x, p4.x);
                const minY = Math.min(p1.y, p2.y, p3.y, p4.y);
                const maxX = Math.max(p1.x, p2.x, p3.x, p4.x);
                const maxY = Math.max(p1.y, p2.y, p3.y, p4.y);

                const props = [obj.color, obj.fillColor, obj.fillType, obj.spacing, obj.angle, obj.segmentLength];
                const isAA = isAxisAligned(p1, p2, p3, p4);

                let mirroredShape;
                if (isAA) {
                    if (isSquare) {
                        const size = Math.max(maxX - minX, maxY - minY);
                        mirroredShape = new Square(minX, minY, size, ...props);
                    } else {
                        mirroredShape = new Rectangle(minX, minY, maxX - minX, maxY - minY, ...props);
                    }
                } else {
                    const contour = [[[[p1.x, p1.y], [p2.x, p2.y], [p3.x, p3.y], [p4.x, p4.y], [p1.x, p1.y]]]];
                    mirroredShape = new PolySpline(contour, ...props);
                }
                mirroredShape.contourStitchType = obj.contourStitchType || 'none';
                mirroredShape.lineWidth = obj.lineWidth || 1;
                return mirroredShape;
            }

            if (typeName === 'Circle' || obj.centerX !== undefined) {
                const mirroredCenter = mirrorPointUtil(obj.centerX, obj.centerY, a, b, c);
                const mirroredShape = new Circle(mirroredCenter.x, mirroredCenter.y, obj.radius, obj.color, obj.fillColor, obj.fillType, obj.spacing, obj.angle, obj.segmentLength);
                mirroredShape.contourStitchType = obj.contourStitchType;
                mirroredShape.lineWidth = obj.lineWidth;
                return mirroredShape;
            }

            if (typeName === 'Ellipse' || (obj.points && Array.isArray(obj.points) && !obj.contours && typeName !== 'PolySpline')) {
                const mirroredPoints = obj.points.map((p) => {
                    const px = p?.x ?? p?.[0] ?? 0;
                    const py = p?.y ?? p?.[1] ?? 0;
                    const mirrored = mirrorPointUtil(px, py, a, b, c);
                    return { x: mirrored.x, y: mirrored.y };
                });
                const mirroredShape = new Ellipse(mirroredPoints, obj.color, obj.fillColor, obj.fillType, obj.spacing, obj.angle, obj.segmentLength);
                mirroredShape.contourStitchType = obj.contourStitchType;
                mirroredShape.lineWidth = obj.lineWidth;
                return mirroredShape;
            }

            if (obj.contours && Array.isArray(obj.contours)) {
                const mirroredContours = obj.contours.map(polygon =>
                    polygon.map(ring =>
                        ring.map(p => {
                            const px = p?.x ?? p?.[0] ?? 0;
                            const py = p?.y ?? p?.[1] ?? 0;
                            const pType = p?.type || 'interpolative';
                            const mirrored = mirrorPointUtil(px, py, a, b, c);
                            return { x: mirrored.x, y: mirrored.y, type: pType };
                        })
                    )
                );
                const mirroredShape = new PolySpline(mirroredContours, obj.color, obj.fillColor, obj.fillType, obj.spacing, obj.angle, obj.segmentLength);
                mirroredShape.contourStitchType = obj.contourStitchType;
                mirroredShape.lineWidth = obj.lineWidth;
                return mirroredShape;
            }

            if (obj.points && Array.isArray(obj.points) && obj.points.length > 0) {
                const mirroredPoints = obj.points.map((p) => {
                    const px = Array.isArray(p) ? p[0] : (p?.x ?? 0);
                    const py = Array.isArray(p) ? p[1] : (p?.y ?? 0);
                    const pType = p?.type || 'interpolative';
                    const mirrored = mirrorPointUtil(px, py, a, b, c);
                    return { x: mirrored.x, y: mirrored.y, type: pType };
                });
                const mirroredShape = new PolySpline(mirroredPoints, obj.color, obj.fillColor, obj.fillType, obj.spacing, obj.angle, obj.segmentLength);
                mirroredShape.contourStitchType = obj.contourStitchType;
                mirroredShape.lineWidth = obj.lineWidth;
                return mirroredShape;
            }

            return null;
        }).filter(obj => obj !== null);

        if (mirroredObjects.length > 0) {
            gridAreaRef.current?.modifyObjects([], mirroredObjects);
            setSelectedObjects(mirroredObjects);
        }

        setIsMirrorMode(false);
        setMode('select');
        setMirrorLinePoints([]);
        setMirrorPreviewPoint(null);
    }, [selectedObjects, gridOffset, gridAreaRef, setSelectedObjects, setIsMirrorMode, setMode, setMirrorLinePoints, setMirrorPreviewPoint]);

    const handleMirror = useCallback((activate) => {
        if (activate) {
            setIsMirrorMode(true);
            setMirrorLinePoints([]);
            setMirrorPreviewPoint(null);
            setMode('mirror');
        } else {
            setIsMirrorMode(false);
            setMirrorLinePoints([]);
            setMirrorPreviewPoint(null);
            setMode('select');
        }
    }, [setMode]);

    return {
        isMirrorMode,
        setIsMirrorMode,
        mirrorLinePoints,
        setMirrorLinePoints,
        mirrorPreviewPoint,
        setMirrorPreviewPoint,
        handleMirror,
        performMirrorOperation
    };
};
